import { graphQLClient } from '@api/client';
import {
  PaymentStatusEnum,
  ProductTypeEnum,
  Treatment,
  TreatmentStatusEnum,
  useCreateDirectTreatmentMutation,
  useCreateTreatmentWithPatientMutation,
  useTreatmentsQuery,
  useUpdateTreatmentCancelMutation
} from '@api/GoodocAPI';
import { useAuthContext } from '@context/Auth';
import { useModalContext } from '@context/Modal';
import { useTreatmentInformationContext } from '@context/TreatmentInformation';
import { useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import {
  AGREE_ALL_BUTTON,
  AGREE_ALL_IN_SPECIFIC_BUTTON,
  AGREE_PERSONAL_INFORMATION_USAGE,
  CANCEL_TREATMENT_BUTTON,
  START_TREATMENT_BUTTON
} from '@pages/PatientSetting/views/PersonalInformationAgreement/focuskey';
import constate from 'constate';
import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { agreements, wholeContentTitle } from './text';
import { usePaymentMethodsContext } from '@context/Payment/PaymentMethods';
import { MATCHING } from '@router/path';
import { getRequestTreatment, getUnpaidTreatment } from '@utils/treatmentUtil';

type AgreedSpecificAgreement = Array<boolean>;
type DisplayAgreements = Array<Agreement>;
type Agreement = { title: string; content: string };

const useAgreement = () => {
  const scrollingRef = useRef<HTMLInputElement>(null);

  const { isSignIn } = useAuthContext();
  const [isSpecificAgreementOpened, setIsSpecificAgreementOpened] = useState(false);
  const [isDisplayWholeContent, setIsDisplayWholeContent] = useState(true);
  const [isAgreedSpecificAgreement, setIsAgreedSpecificAgreement] =
    useState<AgreedSpecificAgreement>([false, false, false, false]);
  const [displayAgreements, setDisplayAgreements] =
    useState<DisplayAgreements>(agreements);
  const [agreementTitle, setAgreementTitle] = useState(wholeContentTitle);
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { visible, modalInfo, openModal, closeModal } = useModalContext();
  const { mutate: createTreatment, isLoading: isLoadingCreateTreatment } =
    useCreateTreatmentWithPatientMutation(graphQLClient);
  const { mutate: createDirectTreatment, isLoading: isLoadingCreateDirectTreatment } =
    useCreateDirectTreatmentMutation(graphQLClient);
  const { selectedPatientId, selectedDoctorId, selectedUntactDepartment } =
    useTreatmentInformationContext();
  const { paymentMethods } = usePaymentMethodsContext();
  const { data: treatmentsResponse } = useTreatmentsQuery(graphQLClient, undefined, {
    enabled: isSignIn
  });

  const { setFocus } = useFocusable();

  const focusAgreeAll = useCallback(() => {
    if (visible) {
      setFocus(modalInfo.initialFocusKey);
    } else if (isSpecificAgreementOpened) {
      setFocus(AGREE_ALL_IN_SPECIFIC_BUTTON);
    } else {
      setFocus(AGREE_ALL_BUTTON);
    }
  }, [isSpecificAgreementOpened, modalInfo.initialFocusKey, setFocus, visible]);

  const focusAgreePersonalInformationUsage = () => {
    if (visible) {
      setFocus(modalInfo.initialFocusKey);
    } else {
      setFocus(AGREE_PERSONAL_INFORMATION_USAGE);
    }
  };

  const { mutateAsync: updateTreatmentCancel } =
    useUpdateTreatmentCancelMutation(graphQLClient);

  const cancelTreatment = useCallback(
    async (treatmentId: string) => {
      await updateTreatmentCancel({ treatmentId });
    },
    [updateTreatmentCancel]
  );

  const startTreatment = async () => {
    if (isLoadingCreateTreatment) return;
    if (paymentMethods.length === 0) return;
    if (!selectedUntactDepartment) return;

    createTreatment(
      {
        patientId: selectedPatientId,
        departmentId: selectedUntactDepartment.id,
        phone: user?.phone,
        symptomDescription: '',
        productType: ProductTypeEnum.Tv
      },
      {
        onSuccess: (response) => {
          closeModal();
          navigate(MATCHING, {
            state: {
              treatmentId: response.createTreatmentWithPatient.id
            }
          });
        }
      }
    );
  };

  const startDirectTreatment = () => {
    if (isLoadingCreateDirectTreatment) return;
    if (paymentMethods.length === 0) return;
    if (!selectedUntactDepartment) return;

    createDirectTreatment(
      {
        patientId: selectedPatientId,
        departmentId: selectedUntactDepartment.id,
        doctorId: selectedDoctorId,
        phone: user?.phone,
        symptomDescription: '',
        productType: ProductTypeEnum.Tv
      },
      {
        onSuccess: (response) => {
          closeModal();
          navigate(MATCHING, {
            state: {
              treatmentId: response.createDirectTreatment.id
            }
          });
        }
      }
    );
  };

  const unPaidTreatment =
    getUnpaidTreatment(treatmentsResponse?.treatments as Treatment[]) ?? undefined;

  const requestTreatment =
    getRequestTreatment(treatmentsResponse?.treatments as Treatment[]) ?? undefined;

  const checkAndStartTreatment = () => {
    if (requestTreatment) {
      return openCancelTreatemtnModal(requestTreatment.id);
    }

    if (unPaidTreatment) {
      return openPaymentModal(unPaidTreatment.id);
    }

    openStartTreatmentModal();
  };

  const openCancelTreatemtnModal = (treatmentId: string) => {
    openModal({
      title: '이미 진료 요청 중입니다. 취소하고 진행하시겠습니까?',
      initialFocusKey: CANCEL_TREATMENT_BUTTON,
      exitFocusKey: AGREE_ALL_BUTTON,
      buttons: [
        {
          text: '진행',
          focusKey: CANCEL_TREATMENT_BUTTON,
          onClick: async () => {
            await cancelTreatment(treatmentId);
            closeModal();
          },
          iconType: 'arrow'
        },
        {
          text: '뒤로 가기',
          onClick: () => {
            closeModal();
          },
          iconType: 'cancel'
        }
      ]
    });
  };

  const openPaymentModal = (treatmentId: string) => {
    openModal({
      title: '결제하지 않은 진료건이 있습니다.\n결제를 완료하고 진료 신청해주세요.',
      initialFocusKey: CANCEL_TREATMENT_BUTTON,
      exitFocusKey: AGREE_ALL_BUTTON,
      buttons: [
        {
          text: '진료내역 보기',
          focusKey: CANCEL_TREATMENT_BUTTON,
          onClick: () => {
            navigate(treatmentId ? `/history/${treatmentId}` : '/history');
            closeModal();
          },
          iconType: 'arrow'
        },
        {
          text: '뒤로 가기',
          onClick: closeModal,
          iconType: 'cancel'
        }
      ]
    });
  };

  const openStartTreatmentModal = () => {
    openModal({
      title: '비대면 진료를 시작하시겠습니까?',
      initialFocusKey: START_TREATMENT_BUTTON,
      exitFocusKey: AGREE_ALL_BUTTON,
      buttons: [
        {
          text: '시작',
          onClick: !!selectedDoctorId
            ? () => startDirectTreatment()
            : () => startTreatment(),
          focusKey: START_TREATMENT_BUTTON,
          iconType: 'arrow'
        },
        {
          text: '취소',
          onClick: () => {
            closeModal();
          },
          iconType: 'cancel'
        }
      ]
    });
  };

  const scrollAgreement = useCallback(
    (direction: 'up' | 'down') => {
      if (scrollingRef?.current === null) {
        return;
      }

      const currentScrollLocation = scrollingRef.current.scrollTop;
      const targetScrollMovingLength = direction === 'up' ? -400 : 400;
      scrollingRef.current.scrollTo({
        top: currentScrollLocation + targetScrollMovingLength,
        behavior: 'smooth'
      });
    },
    [scrollingRef]
  );

  const goPaymentSelection = () => {
    navigate(-1);
  };

  const openSpecificAgreementButtons = () => {
    setIsSpecificAgreementOpened((prev) => !prev);
    focusAgreePersonalInformationUsage();
  };

  const isAllAgreementAgreed = (index: number) => {
    const isOtherAgreementsTrue =
      isAgreedSpecificAgreement.filter((agreed) => agreed).length === 3;
    const isChangedTrue = !isAgreedSpecificAgreement[index];
    return isOtherAgreementsTrue && isChangedTrue;
  };

  const toggleSpecificAgreementState = (index: number): AgreedSpecificAgreement => {
    const copiedPrev = [...isAgreedSpecificAgreement];
    copiedPrev[index] = !copiedPrev[index];
    return copiedPrev;
  };

  const changeSpecificAgreementState = async (index: number) => {
    setIsAgreedSpecificAgreement(toggleSpecificAgreementState(index));
    if (isAllAgreementAgreed(index)) {
      await checkAndStartTreatment();
    }
  };

  const setSpecificAgreement = (index: number) => {
    setIsDisplayWholeContent(false);
    setAgreementTitle(agreements[index].title);
    setDisplayAgreements(agreements.slice(index, index + 1));
  };

  const setWholeAgreement = () => {
    setIsDisplayWholeContent(true);
    setAgreementTitle(wholeContentTitle);
    setDisplayAgreements(agreements);
  };

  return {
    agreements,
    isSpecificAgreementOpened,
    isAgreedSpecificAgreement,
    changeSpecificAgreementState,
    openSpecificAgreementButtons,
    checkAndStartTreatment,
    goPaymentSelection,
    scrollingRef,
    scrollAgreement,
    focusAgreeAll,
    focusAgreePersonalInformationUsage,
    agreementTitle,
    displayAgreements,
    setSpecificAgreement,
    setWholeAgreement,
    isDisplayWholeContent
  };
};

export const [AgreementProvider, useAgreementContext] = constate(useAgreement);
